import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, DollarSign } from "lucide-react";
import { useRecordPaymentForm } from "./useRecordPaymentForm";
import { RECORD_PAYMENT_METHODS } from "./paymentMethods";

function CardFeeBreakdown({ percent, feeCents, netCents }: { percent: number; feeCents: number; netCents: number }) {
  return (
    <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1" aria-live="polite">
      <div className="flex justify-between">
        <span className="text-muted-foreground">Card processing fee ({percent}%)</span>
        <span>${(feeCents / 100).toFixed(2)}</span>
      </div>
      <div className="flex justify-between font-semibold">
        <span>Credited to invoice</span>
        <span>${(netCents / 100).toFixed(2)}</span>
      </div>
    </div>
  );
}

interface RecordPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quoteId: string;
  quoteTotal: number;
  amountPaid: number;
  quoteNumber: string;
}

export function RecordPaymentDialog({
  open,
  onOpenChange,
  quoteId,
  quoteTotal,
  amountPaid,
  quoteNumber,
}: RecordPaymentDialogProps) {
  const f = useRecordPaymentForm({ enabled: open, quoteId, quoteTotal, amountPaid, initialMethod: "cash", onRecorded: () => onOpenChange(false) });
  const handleOpenChange = (o: boolean) => {
    if (!o) f.resetAmount();
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Record Payment — {quoteNumber}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Balance info */}
          <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Quote Total</span>
              <span className="font-medium">${quoteTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Already Paid</span>
              <span className="font-medium">${amountPaid.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-semibold border-t border-border pt-1">
              <span>Balance Remaining</span>
              <span>${f.balance.toFixed(2)}</span>
            </div>
          </div>

          {/* Amount */}
          <div className="space-y-1.5">
            <Label htmlFor={f.idFor("amount")} className="text-xs font-medium">
              {f.isCard ? "Amount charged to card *" : "Amount *"}
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              <Input
                id={f.idFor("amount")}
                type="number"
                step="0.01"
                min="0.01"
                value={f.displayedAmount}
                onChange={(e) => f.setAmount(e.target.value)}
                className="pl-7"
              />
            </div>
            {f.isCard && (
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id={f.idFor("no-card-fee")}
                  checked={f.noCardFee}
                  onCheckedChange={(c) => f.setNoCardFee(c === true)}
                />
                <Label htmlFor={f.idFor("no-card-fee")} className="text-xs font-normal">
                  No card fee was charged on this payment
                </Label>
              </div>
            )}
            {f.applyFee && f.feeStatus === "success" && (
              <CardFeeBreakdown percent={f.percent} feeCents={f.split.feeCents} netCents={f.split.netCents} />
            )}
            {f.applyFee && f.feeStatus === "pending" && (
              <p className="text-xs text-muted-foreground">Loading the card fee setting…</p>
            )}
            {f.applyFee && f.feeStatus === "error" && (
              <p className="text-xs text-destructive">Couldn't load the card fee setting — reload to record a card payment</p>
            )}
          </div>

          {/* Method */}
          <div className="space-y-1.5">
            <Label id={f.idFor("method")} className="text-xs font-medium">Payment Method *</Label>
            <RadioGroup value={f.method} onValueChange={f.handleMethodChange} aria-labelledby={f.idFor("method")} className="grid grid-cols-3 gap-2">
              {RECORD_PAYMENT_METHODS.map((m) => (
                <label
                  key={m.value}
                  className={`flex items-center gap-2 rounded-lg border p-2.5 cursor-pointer text-sm transition-colors ${
                    f.method === m.value ? "border-primary bg-primary/5" : "border-border"
                  }`}
                >
                  <RadioGroupItem value={m.value} />
                  {m.label}
                </label>
              ))}
            </RadioGroup>
          </div>

          {/* Reference */}
          <div className="space-y-1.5">
            <Label htmlFor={f.idFor("reference")} className="text-xs font-medium">Reference # / Confirmation</Label>
            <Input
              id={f.idFor("reference")}
              value={f.reference}
              onChange={(e) => f.setReference(e.target.value)}
              placeholder="e.g. check #1234, e-transfer confirmation"
            />
          </div>

          {/* Payment Date */}
          <div className="space-y-1.5">
            <Label htmlFor={f.idFor("payment-date")} className="text-xs font-medium">Payment Date</Label>
            <Input
              id={f.idFor("payment-date")}
              type="date"
              value={f.paymentDate}
              onChange={(e) => f.setPaymentDate(e.target.value)}
            />
            {f.squareMatch && (
              <p role="alert" className="text-xs text-destructive">
                Square already recorded this payment (${Number(f.squareMatch.amount).toFixed(2)}) on{" "}
                {new Date(f.squareMatch.payment_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}.
                {" "}Recording it again counts it twice.
              </p>
            )}
            {f.isCard && f.squarePaymentsError && (
              <p className="text-xs text-muted-foreground">Couldn't check whether Square already recorded this payment.</p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor={f.idFor("notes")} className="text-xs font-medium">Notes</Label>
            <Textarea
              id={f.idFor("notes")}
              value={f.notes}
              onChange={(e) => f.setNotes(e.target.value)}
              placeholder="Optional memo..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
          <Button onClick={f.submit} disabled={f.isPending || f.feeUnavailable}>
            {f.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <DollarSign className="h-4 w-4 mr-1" />}
            {f.squareMatch ? "Record anyway" : `Record $${(f.creditedCents / 100).toFixed(2)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
